/**
 * Cost of Living Index 2026 — household-basket multipliers across 30
 * cities. NYC = 100.0 baseline. Used by `colMultiplier()` to compute
 * "if we moved from city A to city B, our annual spend changes by Y×".
 *
 * **Source**: NUMBEO Cost of Living Plus Rent Index 2026
 * (https://www.numbeo.com/cost-of-living/rankings_current.jsp).
 * Retrieved 2026-05-08. Values are approximate snapshots and rounded to
 * one decimal. **Spot-check the destination city before relying on this
 * helper for actual relocation decisions** — NUMBEO updates indices
 * roughly quarterly + crowd-sources entries, so individual cities may
 * have drifted since retrieval.
 *
 * **Mirrored from `shared/src/data/cost-of-living.ts`** per the 5.1 lock
 * (web does NOT import from `@hearth/shared`). Edit both files together.
 */

export type CityCode =
  | "NYC" | "SFO" | "BOS" | "SEA" | "DCA" | "SAN" | "MIA" | "DEN" | "PDX"
  | "BOU" | "CHI" | "AVL" | "AUS" | "MSP" | "CLT" | "PHX" | "BNA" | "ATL"
  | "RDU" | "PIT" | "LON" | "SIN" | "AMS" | "TYO" | "BER" | "MAD" | "LIS"
  | "MEX" | "BKK" | "DPS";

export interface CityCol {
  readonly city: string;
  readonly country: string;
  readonly col_index: number;
}

export const COL_INDEX_2026: Readonly<Record<CityCode, CityCol>> = {
  NYC: { city: "New York", country: "USA", col_index: 100.0 },
  SFO: { city: "San Francisco", country: "USA", col_index: 92.5 },
  BOS: { city: "Boston", country: "USA", col_index: 78.4 },
  SEA: { city: "Seattle", country: "USA", col_index: 75.2 },
  DCA: { city: "Washington DC", country: "USA", col_index: 73.8 },
  SAN: { city: "San Diego", country: "USA", col_index: 70.5 },
  MIA: { city: "Miami", country: "USA", col_index: 68.9 },
  DEN: { city: "Denver", country: "USA", col_index: 67.2 },
  PDX: { city: "Portland", country: "USA", col_index: 65.7 },
  BOU: { city: "Boulder", country: "USA", col_index: 64.8 },
  CHI: { city: "Chicago", country: "USA", col_index: 60.4 },
  AVL: { city: "Asheville", country: "USA", col_index: 59.7 },
  AUS: { city: "Austin", country: "USA", col_index: 58.8 },
  MSP: { city: "Minneapolis", country: "USA", col_index: 57.3 },
  CLT: { city: "Charlotte", country: "USA", col_index: 56.4 },
  PHX: { city: "Phoenix", country: "USA", col_index: 56.1 },
  BNA: { city: "Nashville", country: "USA", col_index: 55.9 },
  ATL: { city: "Atlanta", country: "USA", col_index: 55.4 },
  RDU: { city: "Raleigh-Durham", country: "USA", col_index: 53.8 },
  PIT: { city: "Pittsburgh", country: "USA", col_index: 49.2 },
  AMS: { city: "Amsterdam", country: "Netherlands", col_index: 71.4 },
  BKK: { city: "Bangkok", country: "Thailand", col_index: 39.1 },
  BER: { city: "Berlin", country: "Germany", col_index: 64.7 },
  DPS: { city: "Denpasar (Bali)", country: "Indonesia", col_index: 31.8 },
  LIS: { city: "Lisbon", country: "Portugal", col_index: 49.4 },
  LON: { city: "London", country: "UK", col_index: 78.6 },
  MAD: { city: "Madrid", country: "Spain", col_index: 53.2 },
  MEX: { city: "Mexico City", country: "Mexico", col_index: 41.5 },
  SIN: { city: "Singapore", country: "Singapore", col_index: 79.1 },
  TYO: { city: "Tokyo", country: "Japan", col_index: 67.8 },
};

export function colMultiplier(from: CityCode, to: CityCode): number {
  const fromEntry = COL_INDEX_2026[from];
  const toEntry = COL_INDEX_2026[to];
  if (!fromEntry) {
    throw new Error(`colMultiplier: unknown 'from' city code: ${from}`);
  }
  if (!toEntry) {
    throw new Error(`colMultiplier: unknown 'to' city code: ${to}`);
  }
  return toEntry.col_index / fromEntry.col_index;
}

export const ALL_CITY_CODES: readonly CityCode[] = (
  Object.keys(COL_INDEX_2026) as CityCode[]
).sort((a, b) =>
  COL_INDEX_2026[a].city.localeCompare(COL_INDEX_2026[b].city),
);
